/**
 * Create Subscription Dialog
 * 
 * Allows provider admins to create new subscriptions by selecting tenant and product
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Stack,
  InputAdornment,
  Autocomplete,
  Divider,
  Paper,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { Search as SearchIcon, Warning as WarningIcon } from '@mui/icons-material';
import { providerStripeService } from '../../services/ProviderStripeService';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { getBillingResourceLabel, getFeatureLabel } from '@cnapse/shared';
import * as schemas from '../../../../shared/src/schemas';
import * as dt from '../../../../shared/src/data.types';

type StripeProductPrice = schemas.StripeProductResponse['prices'][number];

interface ProductAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Optional: If provided, pre-select this tenant
  tenantId?: string;
  tenantName?: string;
  currentProductId?: string | null;
}

const ProductAssignmentDialog: React.FC<ProductAssignmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  tenantId: preSelectedTenantId,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [products, setProducts] = useState<schemas.StripeProductResponse[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [existingSubscriptions, setExistingSubscriptions] = useState<schemas.StripeSubscriptionItem[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load tenants and products
  useEffect(() => {
    if (open) {
      loadInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when dialog opens
  }, [open]);

  // Load existing subscriptions when tenant is selected
  useEffect(() => {
    if (selectedTenantId) {
      loadTenantSubscriptions(selectedTenantId);
    } else {
      setExistingSubscriptions([]);
    }
  }, [selectedTenantId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load tenants and products in parallel
      const [tenantsResponse, productsResponse] = await Promise.all([
        providerTenantsService.getTenants(),
        providerStripeService.listStripeProducts({ active: true }),
      ]);

      setTenants(tenantsResponse);
      setProducts(productsResponse.data.products || []);
      
      // Pre-select tenant if provided
      if (preSelectedTenantId) {
        setSelectedTenantId(preSelectedTenantId);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantSubscriptions = async (tenantId: string) => {
    try {
      setLoadingSubscriptions(true);
      
      const response = await providerStripeService.listStripeSubscriptions({
        tenantId: tenantId as dt.Uuid,
        // Only show active and trialing subscriptions
      });
      
      // Filter for active or trialing subscriptions
      const activeSubscriptions = response.subscriptions.filter(
        (sub) =>
          sub.hasAccess === true ||
          (sub.hasAccess === undefined &&
            (sub.status === dt.SUBSCRIPTION_STATUS.Active ||
              sub.status === dt.SUBSCRIPTION_STATUS.Trialing)),
      );
      
      setExistingSubscriptions(activeSubscriptions);
    } catch (err) {
      console.error('Failed to load tenant subscriptions:', err);
      // Don't set error here, just log it - not critical to the flow
      setExistingSubscriptions([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTenantId) {
      setError('Please select a tenant');
      return;
    }

    if (!selectedProductId) {
      setError('Please select a product');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get the selected product to find price IDs
      const selectedProduct = products.find(p => p.id === selectedProductId);
      if (!selectedProduct || !selectedProduct.prices || selectedProduct.prices.length === 0) {
        throw new Error('Selected product has no prices configured');
      }

      // Extract all price IDs from the product
      const priceIds = selectedProduct.prices.map((price: { id: string }) => price.id);

      await providerStripeService.assignStripeSubscription({
        tenantId: selectedTenantId as dt.Uuid,
        productId: selectedProductId,
        priceIds,
      });

      // Call success callback and wait for it to complete
      // This ensures the parent component refreshes data before dialog closes
      await onSuccess();
      
      handleClose();
    } catch (err) {
      console.error('Failed to create subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to create subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedTenantId('');
      setSelectedProductId('');
      setExistingSubscriptions([]);
      setError(null);
      setSearchTerm('');
      onClose();
    }
  };

  const filteredProducts = products.filter((product) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(search) ||
      (product.description && product.description.toLowerCase().includes(search)) ||
      product.id.toLowerCase().includes(search)
    );
  });

  const formatPrice = (price: {
    unit_amount: number | null;
    currency: string;
    recurring: { interval: string; usage_type?: string } | null;
    metadata?: Record<string, string>;
  }): string => {
    if (!price.unit_amount) return 'Free';
    
    const amount = price.unit_amount / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: price.currency.toUpperCase(),
    }).format(amount);

    let label = '';
    
    // Determine what this price is for based on metadata
    if (price.metadata?.resource_type === 'base') {
      label = `${getBillingResourceLabel('base')} Price`;
    } else if (price.metadata?.resource_type === 'wafers') {
      label = `${getBillingResourceLabel('wafers')} Overage`;
    } else if (price.metadata?.resource_type === 'containers') {
      label = `${getBillingResourceLabel('containers')} Overage`;
    } else if (price.metadata?.resource_type === 'users') {
      label = `${getBillingResourceLabel('users')} Overage`;
    } else if (price.metadata?.resource_type === 'axon_gateways') {
      label = `${getBillingResourceLabel('axon_gateways')} Overage`;
    } else if (price.metadata?.resource_type === 'storage') {
      label = `${getBillingResourceLabel('storage')} Overage`;
    } else if (price.metadata?.resource_type === 'encryption') {
      label = `${getBillingResourceLabel('encryption')} Add-on`;
    } else if (price.metadata?.resource_type === 'feature') {
      label = getFeatureLabel(price.metadata?.feature_name);
    }

    if (price.recurring) {
      return `${label}: ${formatted}/${price.recurring.interval}`;
    }
    return `${label}: ${formatted}`;
  };

  const getProductPriceDisplay = (product: schemas.StripeProductResponse): string => {
    if (!product.prices || product.prices.length === 0) return 'No pricing';
    
    // Find the base recurring price (not metered)
    const basePrice = product.prices.find(
      p => p.recurring && p.recurring.usage_type === 'licensed'
    );
    
    if (basePrice) {
      return formatPrice(basePrice);
    }
    
    // Fallback to first price
    return formatPrice(product.prices[0]);
  };

  const getStatusChip = (status: string) => {
    const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      active: 'success',
      trialing: 'info',
      past_due: 'warning',
      canceled: 'error',
      incomplete: 'warning',
      paused: 'default',
    };

    return (
      <Chip
        label={status.replace('_', ' ').toUpperCase()}
        size="small"
        color={statusColors[status] || 'default'}
      />
    );
  };

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Subscription
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Tenant Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Step 1: Select Tenant
              </Typography>
              <Autocomplete
                fullWidth
                options={tenants}
                getOptionLabel={(option) => `${option.name} (${option.subdomain})`}
                value={selectedTenant || null}
                onChange={(_, newValue) => {
                  setSelectedTenantId(newValue?.id || '');
                }}
                disabled={submitting || !!preSelectedTenantId}
                renderInput={(params) => (
                  <AppTextField
                    {...params}
                    label="Tenant"
                    placeholder="Search for tenant by name or subdomain..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.subdomain}.waferinventory.com
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
              />
            </Box>

            {/* Existing Subscriptions Warning */}
            {selectedTenantId && loadingSubscriptions && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Checking existing subscriptions...
                </Typography>
              </Box>
            )}

            {selectedTenantId && !loadingSubscriptions && existingSubscriptions.length > 0 && (
              <Alert severity="warning" icon={<WarningIcon />}>
                <Typography variant="subtitle2" gutterBottom>
                  This tenant has {existingSubscriptions.length} active subscription{existingSubscriptions.length !== 1 ? 's' : ''}:
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {existingSubscriptions.map((sub) => (
                    <Paper key={sub.subscriptionId ?? sub.stripeSubscriptionId} sx={{ p: 1.5, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {sub.productName}
                        </Typography>
                        {getStatusChip(sub.status)}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Period:{' '}
                        {sub.currentPeriodStart
                          ? new Date(sub.currentPeriodStart).toLocaleDateString()
                          : '—'}{' '}
                        -{' '}
                        {sub.currentPeriodEnd
                          ? new Date(sub.currentPeriodEnd).toLocaleDateString()
                          : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ${sub.monthlyAmount.toFixed(2)}/month
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Creating a new subscription will add to the tenant's billing. Consider canceling existing subscriptions first if needed.
                </Typography>
              </Alert>
            )}

            <Divider />

            {/* Product Selection */}
            {selectedTenantId && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Step 2: Select Product
                </Typography>

            {products.length === 0 ? (
              <Alert severity="warning">
                No active products available. Please create products in the Products page first.
              </Alert>
            ) : (
              <>
                <AppTextField
                  fullWidth
                  size="small"
                  placeholder="Search products by name, description, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={selectedProductId}
                    label="Product"
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    disabled={submitting}
                  >
                    {filteredProducts.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          No products match your search
                        </Typography>
                      </MenuItem>
                    ) : (
                      filteredProducts.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1">{product.name}</Typography>
                          {product.description && (
                            <Typography variant="caption" color="text.secondary">
                              {product.description}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={getProductPriceDisplay(product)}
                          size="small"
                          color="default"
                        />
                      </Box>
                    </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {searchTerm && filteredProducts.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Showing {filteredProducts.length} of {products.length} product{products.length !== 1 ? 's' : ''}
                  </Typography>
                )}
              </>
            )}
              </Box>
            )}

            {/* Selected Product Details */}
            {selectedProductId && (
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Product Details
                </Typography>
                {(() => {
                  const selected = products.find((p: schemas.StripeProductResponse) => p.id === selectedProductId);
                  if (!selected) return null;
                  
                  return (
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {selected.name}
                      </Typography>
                      {selected.description && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {selected.description}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Pricing Structure:</strong>
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        {/* Base Price */}
                        {selected.prices?.filter((p: StripeProductPrice) => p.metadata?.resource_type === 'base').map((price: StripeProductPrice, idx: number) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                            • {formatPrice(price)}
                          </Typography>
                        ))}
                        
                        {/* Overage Prices */}
                        {selected.prices?.filter((p: StripeProductPrice) => 
                          ['wafers', 'containers', 'users', 'axon_gateways', 'storage'].includes(p.metadata?.resource_type)
                        ).length > 0 && (
                          <>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', fontWeight: 'medium' }}>
                              Overage Charges:
                            </Typography>
                            {selected.prices?.filter((p: StripeProductPrice) => 
                              ['wafers', 'containers', 'users', 'axon_gateways', 'storage'].includes(p.metadata?.resource_type)
                            ).map((price: StripeProductPrice, idx: number) => (
                              <Typography key={idx} variant="caption" display="block" sx={{ pl: 1 }}>
                                • {formatPrice(price)} (metered)
                              </Typography>
                            ))}
                          </>
                        )}
                        
                        {/* Feature Add-ons */}
                        {selected.prices?.filter((p: StripeProductPrice) => p.metadata?.resource_type === 'feature').length > 0 && (
                          <>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', fontWeight: 'medium' }}>
                              Feature Add-ons:
                            </Typography>
                            {selected.prices?.filter((p: StripeProductPrice) => p.metadata?.resource_type === 'feature').map((price: StripeProductPrice, idx: number) => (
                              <Typography key={idx} variant="caption" display="block" sx={{ pl: 1 }}>
                                • {formatPrice(price)}
                              </Typography>
                            ))}
                          </>
                        )}
                        
                        {/* Encryption Add-on */}
                        {selected.prices?.filter((p: StripeProductPrice) => p.metadata?.resource_type === 'encryption').map((price: StripeProductPrice, idx: number) => (
                          <Typography key={idx} variant="caption" display="block" sx={{ mt: 1, pl: 1, color: 'warning.main' }}>
                            • {formatPrice(price)}
                          </Typography>
                        ))}
                      </Box>
                    </Stack>
                  );
                })()}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedTenantId || !selectedProductId || submitting || loading}
        >
          {submitting ? <CircularProgress size={24} /> : 'Create Subscription'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProductAssignmentDialog;
